import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './OnboardingTour.css';

/**
 * Tour de onboarding que NAVEGA pelas telas do painel.
 * A cada "Próximo", leva o lojista para a rota daquele passo e mostra a explicação por cima.
 * A caixa fica num canto (não bloqueia a tela) para o usuário ver a página atrás.
 * O `icon` do passo é aceito por compatibilidade, mas não é exibido.
 *
 * Props:
 *   steps: [{ title, description, icon, route }]
 *   onFinish: () => void
 */
const OnboardingTour = ({ steps = [], onFinish }) => {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [visible, setVisible] = useState(true);

  const finish = useCallback(() => {
    setVisible(false);
    if (onFinish) onFinish();
  }, [onFinish]);

  const goToStep = useCallback((index) => {
    const clamped = Math.max(0, Math.min(index, steps.length - 1));
    setCurrent(clamped);
    const route = steps[clamped]?.route;
    if (route) navigate(route);
  }, [steps, navigate]);

  // Navega para a rota do passo inicial ao montar
  useEffect(() => {
    const route = steps[0]?.route;
    if (route) navigate(route);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Atalhos de teclado
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') finish();
      if (e.key === 'ArrowRight') goToStep(current + 1);
      if (e.key === 'ArrowLeft') goToStep(current - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [finish, goToStep, current]);

  if (!visible || steps.length === 0) return null;

  const step = steps[current];
  const isLast = current === steps.length - 1;

  return (
    <>
      <div className="onb-spotlight" />

      <div className="onb-floating" role="dialog" aria-label="Tour de boas-vindas">
        <button type="button" className="onb-skip" onClick={finish} aria-label="Pular tour">Pular</button>

        <div className="onb-counter">{current + 1} de {steps.length}</div>

        <div className="onb-texts">
          <h2 className="onb-title">{step.title}</h2>
          <p className="onb-desc">{step.description}</p>
        </div>

        <div className="onb-actions">
          {current > 0 ? (
            <button type="button" className="ap-btn ap-btn-sm ap-btn-secondary" onClick={() => goToStep(current - 1)}>
              Voltar
            </button>
          ) : (
            <button type="button" className="ap-btn ap-btn-sm ap-btn-secondary" onClick={finish}>
              Pular
            </button>
          )}

          {!isLast ? (
            <button type="button" className="ap-btn ap-btn-sm ap-btn-primary" onClick={() => goToStep(current + 1)}>
              Próximo
            </button>
          ) : (
            <button type="button" className="ap-btn ap-btn-sm ap-btn-primary" onClick={finish}>
              Começar a usar
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default OnboardingTour;
